// ── Shared element renderer for footer (dark background) ─────────────────────
import { Phone, Mail } from 'lucide-react';

const SOCIAL_ABBR  = { facebook:'FB', instagram:'IG', twitter:'X', youtube:'YT', tiktok:'TT', pinterest:'PT', whatsapp:'WA', linkedin:'LI' };
const SOCIAL_HOVER = { facebook:'hover:bg-blue-600', instagram:'hover:bg-pink-500', twitter:'hover:bg-black', youtube:'hover:bg-red-600', tiktok:'hover:bg-neutral-800', whatsapp:'hover:bg-green-500', linkedin:'hover:bg-blue-700' };

export function renderFooterElement(el) {
  const { type, props = {} } = el;
  switch (type) {
    case 'logo':
      return (
        <a href={props.linkUrl || '/'} className="block mb-4">
          {props.logoType === 'image' && props.logoUrl
            ? <img src={props.logoUrl} alt={props.logoText || 'Logo'} className="h-10 w-auto object-contain" />
            : <span className="text-2xl font-black bg-gradient-to-r from-primary to-accent1 bg-clip-text text-transparent">{props.logoText || 'KiddieJoy'}</span>
          }
        </a>
      );
    case 'text':
      return (
        <div style={{ color: props.color ?? (props.variant === 'heading' ? '#ffffff' : '#9ca3af') }}
          className={
            props.variant === 'heading'   ? 'font-black text-lg mb-6' :
            props.variant === 'small'     ? 'text-xs' :
            props.variant === 'copyright' ? 'text-sm' :
            'text-sm leading-relaxed mb-4'
          }>
          {props.text || 'Text'}
        </div>
      );
    case 'menu': {
      // Resolve links from custom list OR pre-loaded WP nav menus (window.themeData.navMenuItems)
      const menuSource = props.menuSource || (props.links?.length ? 'custom' : 'wp');
      const links = menuSource === 'custom'
        ? (props.links ?? [])
        : (window.themeData?.navMenuItems?.[props.menuId] ?? []).map(i => ({ id: String(i.id), label: i.label, url: i.url || '#' }));
      const linkColor = props.color ?? '#9ca3af';

      if (props.layout === 'horizontal') {
        return (
          <nav className="flex gap-4 flex-wrap">
            {links.map(l => <a key={l.id} href={l.url || '#'} className="text-sm hover:text-primary transition-colors" style={{ color: linkColor }}>{l.label}</a>)}
          </nav>
        );
      }
      return (
        <ul className="space-y-2.5">
          {links.map(l => (
            <li key={l.id}><a href={l.url || '#'} className="text-sm hover:text-primary transition-colors" style={{ color: linkColor }}>{l.label}</a></li>
          ))}
        </ul>
      );
    }
    case 'social':
      return (
        <div className="flex gap-3 flex-wrap">
          {(props.links ?? []).map(sl => (
            <a key={sl.id} href={sl.url || '#'} target="_blank" rel="noreferrer"
              className={`w-11 h-11 bg-white/10 ${SOCIAL_HOVER[sl.platform] ?? 'hover:bg-primary'} rounded-xl flex items-center justify-center font-bold text-sm text-white transition-colors`}>
              {SOCIAL_ABBR[sl.platform] ?? '?'}
            </a>
          ))}
        </div>
      );
    case 'newsletter':
      return (
        <div>
          <div className="flex gap-2">
            <input type="email" placeholder={props.placeholder || 'Enter your email'} className="flex-1 bg-white/10 border border-white/20 rounded-xl px-3 py-2 text-sm text-white placeholder-white/40 outline-none focus:border-primary" />
            <button className="px-4 py-2 rounded-xl text-sm font-bold text-white transition-colors" style={{ backgroundColor: props.buttonColor || '#8B5CF6' }}>
              {props.buttonText || 'Subscribe'}
            </button>
          </div>
        </div>
      );
    case 'contact': {
      const isPhone = props.icon === 'phone' || props.icon !== 'email';
      const href    = isPhone
        ? `tel:${(props.value || '').replace(/[\s()-]/g, '')}`
        : `mailto:${props.value || ''}`;
      return (
        <a href={href} className="flex items-center gap-2 text-sm hover:text-primary transition-colors" style={{ color: props.color || '#9ca3af' }}>
          {isPhone ? <Phone size={14} /> : <Mail size={14} />}
          <span>{props.value}</span>
        </a>
      );
    }
    case 'html':
      return <div className="text-gray-400" dangerouslySetInnerHTML={{ __html: props.html || '' }} />;
    case 'spacer':
      return props.flex ? <div className="flex-1" /> : <div style={{ height: props.width || '16px' }} />;
    case 'divider':
      return <hr className="border-gray-700 my-2" />;
    default:
      return null;
  }
}

// ── Default footer settings ───────────────────────────────────────────────────

const uid = () => Math.random().toString(36).slice(2);

export const DEFAULT_FOOTER_DARK = {
  variant: 'dark',
  mainSection: {
    style: { bgFrom: '#111827', bgTo: '#000000' },
    columns: [
      {
        id: 'fcol-1', flex: 2,
        elements: [
          { id: 'logo-f1', type: 'logo',   props: { logoType: 'text', logoText: 'KiddieJoy', logoUrl: '' } },
          { id: 'text-f1', type: 'text',   props: { text: 'Bringing joy, learning, and endless fun to children through high-quality toys, cycles, and educational products.', variant: 'body' } },
          { id: 'soc-f1',  type: 'social', props: { links: [{ id: 's1', platform: 'instagram', url: '#' }, { id: 's2', platform: 'facebook', url: '#' }] } },
        ]
      },
      {
        id: 'fcol-2', flex: 1,
        elements: [
          { id: 'h-f2', type: 'text', props: { text: 'Quick Links', variant: 'heading' } },
          { id: 'm-f2', type: 'menu', props: { links: [{ id: 'l1', label: 'About Us', url: '#' }, { id: 'l2', label: 'Shop All', url: '#' }, { id: 'l3', label: 'New Arrivals', url: '#' }, { id: 'l4', label: 'Contact', url: '#' }], layout: 'vertical' } },
        ]
      },
      {
        id: 'fcol-3', flex: 1,
        elements: [
          { id: 'h-f3', type: 'text', props: { text: 'Categories', variant: 'heading' } },
          { id: 'm-f3', type: 'menu', props: { links: [{ id: 'l5', label: 'Toys', url: '#' }, { id: 'l6', label: 'Cycles', url: '#' }, { id: 'l7', label: 'Books', url: '#' }], layout: 'vertical' } },
        ]
      },
      {
        id: 'fcol-4', flex: 1,
        elements: [
          { id: 'h-f4', type: 'text', props: { text: 'Support', variant: 'heading' } },
          { id: 'm-f4', type: 'menu', props: { links: [{ id: 'l8', label: 'Help Center', url: '#' }, { id: 'l9', label: 'Shipping', url: '#' }, { id: 'l10', label: 'Returns', url: '#' }], layout: 'vertical' } },
        ]
      },
    ]
  },
  bottomBar: {
    enabled: true,
    style: { borderTop: true },
    left:  [{ id: 'copy-l', type: 'text', props: { text: `© ${new Date().getFullYear()} KiddieJoy Store. All rights reserved.`, variant: 'small', color: '#6b7280' } }],
    right: [{ id: 'copy-r', type: 'menu', props: { links: [{ id: 'p1', label: 'Privacy Policy', url: '#' }, { id: 'p2', label: 'Terms of Service', url: '#' }], layout: 'horizontal', color: '#6b7280' } }],
  },
  customHtml: '',
};

// ── Normalise saved settings into the rows array format ───────────────────────
function resolveRows(s) {
  // New format: s.rows is a non-empty array
  if (Array.isArray(s.rows) && s.rows.length > 0) {
    return s.rows;
  }
  // Old format: s.mainSection single object → wrap it as one row
  if (s.mainSection) {
    return [{
      id: 'legacy-main',
      label: 'Main Footer',
      style: {
        bgFrom: s.mainSection.style?.bgFrom ?? '#111827',
        bgTo:   s.mainSection.style?.bgTo   ?? '#000000',
        padding: '80px 0 40px',
      },
      columns: s.mainSection.columns ?? [],
    }];
  }
  // Fallback: use default
  return resolveRows(DEFAULT_FOOTER_DARK);
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function FooterDark({ settings = {} }) {
  const s      = Object.keys(settings).length ? settings : DEFAULT_FOOTER_DARK;
  const rows   = resolveRows(s);
  const bottom = s.bottomBar ?? DEFAULT_FOOTER_DARK.bottomBar;

  return (
    <footer className="text-white">

      {/* ── Rows (each has its own gradient + columns) ── */}
      {rows.map((row, idx) => {
        const bgFrom      = row.style?.bgFrom      ?? '#111827';
        const bgTo        = row.style?.bgTo        ?? '#000000';
        const padding     = row.style?.padding     ?? (idx === 0 ? '80px 0 40px' : '40px 0');
        const borderBottom = row.style?.borderBottom;
        const borderColor  = row.style?.borderColor ?? '#374151';

        return (
          <div
            key={row.id}
            style={{
              background:    `linear-gradient(to bottom, ${bgFrom}, ${bgTo})`,
              padding,
              borderBottom:  borderBottom ? `1px solid ${borderColor}` : undefined,
            }}
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex gap-8 flex-wrap items-start">
                {(row.columns ?? []).map(col => {
                  const align  = col.align  ?? 'start';
                  const valign = col.valign ?? 'start';
                  const alignClass  = align  === 'center' ? 'items-center text-center' : align  === 'end' ? 'items-end text-right ml-auto' : 'items-start';
                  const valignClass = valign === 'center' ? 'justify-center' : valign === 'end' ? 'justify-end' : 'justify-start';
                  return (
                    <div key={col.id} style={{ flex: col.flex ?? 1, minWidth: 0 }} className={`flex flex-col gap-1 ${alignClass} ${valignClass}`}>
                      {(col.elements ?? []).map(el => (
                        <div key={el.id}>{renderFooterElement(el)}</div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      {/* ── Custom HTML injection ── */}
      {s.customHtml && (
        <div style={{ background: '#111827' }}>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4"
            dangerouslySetInnerHTML={{ __html: s.customHtml }}
          />
        </div>
      )}

      {/* ── Bottom bar ── */}
      {bottom.enabled !== false && (
        <div
          style={{
            background:  '#111827',
            borderTop:   bottom.style?.borderTop
              ? `1px solid ${bottom.style?.borderColor ?? '#374151'}`
              : 'none',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-4">
              {(bottom.left ?? []).map(el => <span key={el.id}>{renderFooterElement(el)}</span>)}
            </div>
            <div className="flex items-center gap-4">
              {(bottom.right ?? []).map(el => <span key={el.id}>{renderFooterElement(el)}</span>)}
            </div>
          </div>
        </div>
      )}

    </footer>
  );
}
